import { Action } from 'redux';
import { put, select } from 'redux-saga/effects';
import { ACTION_CREATOR_NAME, ErrorAction } from '@sofology/sofologycomponents';

import { errorToBasketErrorModel, paymentHelper } from 'helpers';
import { SofologyComponentsToIlpQuestionaryNames, addressToIlpAddress, getFlatObject, isMobilePhoneNumber } from 'helpers/dynamicFormHelpers';
import { DeliveryDetailsModel, IfcAddressDescription, IfcApplicationData, IlpQuestionDescriptions, areAddressesEqual } from 'models';
import { CheckoutActionTypes, CheckoutActions } from 'store/checkout';
import { AppState } from 'store/root.types';
import { BasketSagaReturnType } from 'store/sagaHelpers';

// should be called after checkout page reloading - to try to restore finance form data from local storage
// or after submittion Customer details form - because if customer delivery details was changed then saved finance form is not actual anymore
export interface InitIfcApplicationDataAction extends Action<CheckoutActionTypes.INIT_IFC_APPLICATION_DATA> {
    customerDeliveryData: DeliveryDetailsModel | null;
    questionSet: IlpQuestionDescriptions;
}

export interface InitIfcApplicationDataActionStart extends Action<CheckoutActionTypes.INIT_IFC_APPLICATION_DATA_START> {
}

export interface InitIfcApplicationDataActionSuccess extends Action<CheckoutActionTypes.INIT_IFC_APPLICATION_DATA_SUCCESS> {
    ifcApplicationData: IfcApplicationData | null;
}

export interface InitIfcApplicationDataActionError extends ErrorAction<CheckoutActionTypes.INIT_IFC_APPLICATION_DATA_ERROR> {
}

export const initIfcApplicationData: (questionSet: IlpQuestionDescriptions, customerDeliveryData: DeliveryDetailsModel | null) => InitIfcApplicationDataAction = (questionSet: IlpQuestionDescriptions, customerDeliveryData: DeliveryDetailsModel | null) => ({
    type: CheckoutActionTypes.INIT_IFC_APPLICATION_DATA,
    customerDeliveryData,
    questionSet
});
initIfcApplicationData[ACTION_CREATOR_NAME] = 'initIfcApplicationData';

export type InitIfcApplicationDataFunc = (...args: Parameters<typeof initIfcApplicationData>) => void;

export function* initIfcApplicationDataSaga({ questionSet, customerDeliveryData }: InitIfcApplicationDataAction): BasketSagaReturnType {
    yield put<CheckoutActions>({
        type: CheckoutActionTypes.INIT_IFC_APPLICATION_DATA_START
    });

    try {
        let state: AppState = yield select();

        let ifcApplicationData: IfcApplicationData | null = null;

        const isIfc = paymentHelper.isCreditRelatedPaymentType(state.paymentState.paymentDetails.paymentType);

        if (isIfc) {
            ifcApplicationData = customerDeliveryData ? createIfcApplicationData(questionSet, customerDeliveryData) : getFlatObject(questionSet, {});
        }

        yield put<CheckoutActions>({
            type: CheckoutActionTypes.INIT_IFC_APPLICATION_DATA_SUCCESS,
            ifcApplicationData
        });

        return true;
    } catch (error) {
        yield put<CheckoutActions>({
            type: CheckoutActionTypes.INIT_IFC_APPLICATION_DATA_ERROR,
            error: errorToBasketErrorModel(error, 'initIfcApplicationDataSaga')
        });

        return false;
    }
}

function createIfcApplicationData(questionSet: IlpQuestionDescriptions, customerDeliveryData: DeliveryDetailsModel): IfcApplicationData {
    let ifcApplicationData = getFlatObject(questionSet, {});

    ifcApplicationData[SofologyComponentsToIlpQuestionaryNames.CustomerTitle] = customerDeliveryData.title;
    ifcApplicationData[SofologyComponentsToIlpQuestionaryNames.CustomerFirstName] = customerDeliveryData.firstName;
    ifcApplicationData[SofologyComponentsToIlpQuestionaryNames.CustomerSurname] = customerDeliveryData.surname;

    if (customerDeliveryData.phone && isMobilePhoneNumber(customerDeliveryData.phone)) {
        ifcApplicationData[SofologyComponentsToIlpQuestionaryNames.CustomerPhone] = customerDeliveryData.phone;
    }

    ifcApplicationData[SofologyComponentsToIlpQuestionaryNames.BankAccountHolderName] = `${customerDeliveryData.firstName} ${customerDeliveryData.surname}`.slice(0, 50);

    const billingAddress = customerDeliveryData.billingAddress!;
    const deliveryAddress = customerDeliveryData.deliveryAddress!;

    const isDeliveryAddressDifferentFromBilling = !areAddressesEqual(billingAddress, deliveryAddress);
    const differentDeliveryAddress = isDeliveryAddressDifferentFromBilling ? 'yes' : 'no';

    ifcApplicationData = {
        ...ifcApplicationData,
        ...addressToIlpAddress(billingAddress, questionSet.address.properties.currentAddress as unknown as IfcAddressDescription),
        [SofologyComponentsToIlpQuestionaryNames.DifferentDeliveryAddress]: differentDeliveryAddress
    };

    if (isDeliveryAddressDifferentFromBilling) {
        ifcApplicationData = {
            ...ifcApplicationData,
            ...addressToIlpAddress(deliveryAddress, questionSet.address.properties.deliveryAddress as unknown as IfcAddressDescription)
        };
    }

    return ifcApplicationData;
}